import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { UIProvider } from './context/UIContext'; // <-- IMPORT THE NEW PROVIDER
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import dbService from './services/dbService';

// Initial Data
import { users as initialUsers } from './data/mockUsers';
import { cases as initialCases } from './data/mockCases';
import { rules as initialRules } from './data/mockRules';
import { customers as initialCustomers } from './data/mockCustomers';
import { watchlists as initialWatchlists } from './data/mockWatchlists'; // <-- This now works

// Layout & Auth
import Login from './components/auth/Login';
import PrivateRoute from './components/auth/PrivateRoute';
import MainLayout from './components/layout/MainLayout';

// Module Components
import Dashboard from './components/modules/Dashboard/Dashboard';
import AlertQueue from './components/modules/Alerts/AlertQueue';
// import TransactionGenerator from './components/modules/Transactions/TransactionGenerator';
import TransactionAnalysis from './components/modules/Transactions/TransactionAnalysis';
import CaseList from './components/modules/CaseManagement/CaseList';
import CaseDetail from './components/modules/CaseManagement/CaseDetail';
import KYCProfile from './components/modules/KYC/KYCProfile';
import UserList from './components/modules/UserAdmin/UserList';
import ReportDashboard from './components/modules/Reporting/ReportDashboard';
import RuleList from './components/modules/RuleEngine/RuleList';
import CustomerScreening from './components/modules/Screening/CustomerScreening';
import DataMapping from './components/modules/DataMapping/DataMapping';
import Masters from './components/modules/Masters/Masters';
import AuditLog from './components/modules/Audit/AuditLog';
import PermissionManagement from './components/modules/Permissions/PermissionManagement'; // New import
import AutomationStudio from './components/modules/Automation/AutomationStudio'; // New import
import RiskManagementStudio from './components/modules/Risk/RiskManagementStudio'; // New import
import ProfilePage from './components/modules/Profile/ProfilePage'; // <-- IMPORT THE NEW MODULE
import ScreeningStudio from './components/modules/Screening/ScreeningStudio'; // New import

// import DemoDataGenerator from './components/modules/DemoData/DemoDataGenerator';
import LinkAnalysis from './components/modules/LinkAnalysis/LinkAnalysis';
import Integrations from './components/modules/Integrations/Integrations';

import SystemSimulator from './components/modules/Simulator/SystemSimulator'; // New unified module


const initialPermissions = [
    { role: 'L1 Maker', permissions: { canViewDashboard: true, canViewAlerts: true, canWorkOnCases: true, canAssignCases: true, canViewUsers: true, canEditUsers: false, canDeleteUsers: false, canCreateUsers: false, canViewRules: true, canEditRulesInSandbox: false, canDeployRules: false, canDeleteRules: false, canManageMasters: false }},
    { role: 'L2 Checker', permissions: { canViewDashboard: true, canViewAlerts: true, canWorkOnCases: true, canAssignCases: true, canViewUsers: true, canEditUsers: false, canDeleteUsers: false, canCreateUsers: false, canViewRules: true, canEditRulesInSandbox: true, canDeployRules: false, canDeleteRules: false, canManageMasters: false }},
    { role: 'L3 Admin', permissions: { canViewDashboard: true, canViewAlerts: true, canWorkOnCases: true, canAssignCases: true, canViewUsers: true, canEditUsers: true, canDeleteUsers: true, canCreateUsers: true, canViewRules: true, canEditRulesInSandbox: true, canDeployRules: true, canDeleteRules: true, canManageMasters: true }}
];

const initialMappings = [
    { 
        id: 'kyc',
        sourceFields: [
            'CUST_FNAME', 'CUST_LNAME', 'DOB', 'ENTITY_TYPE',
            'ADDR_LINE1', 'ADDR_CITY', 'ADDR_STATE', 'ADDR_ZIP', 'ADDR_COUNTRY',
            'NATIONALITY', 'OCCUPATION_CD', 'ANNUAL_INCOME', 'SOURCE_OF_WEALTH'
        ],
        targetFields: [
            '(Unmapped)', 'fullName', 'dateOfBirth', 'entityType', 'address', 
            'nationality', 'occupation', 'annualIncome', 'sourceOfWealth'
        ],
        currentMap: {
            'CUST_FNAME': 'fullName', // Mapped to the same target to indicate combination
            'CUST_LNAME': 'fullName',
            'DOB': 'dateOfBirth',
            'ENTITY_TYPE': 'entityType',
            'ADDR_LINE1': 'address', // All address parts map to the 'address' target
            'ADDR_CITY': 'address',
            'ADDR_STATE': 'address',
            'ADDR_ZIP': 'address',
            'ADDR_COUNTRY': 'address',
            'NATIONALITY': 'nationality',
            'OCCUPATION_CD': 'occupation',
            'ANNUAL_INCOME': 'annualIncome',
            'SOURCE_OF_WEALTH': 'sourceOfWealth',
        }
    },
    {
        id: 'transaction',
        sourceFields: [
            'TRN_DATE', 'CUST_ID', 'TRN_AMT_LCY', 'TRN_TYPE_CD', 
            'BEN_NAME', 'BEN_BANK', 'BENEFICIARY_COUNTRY', 'MEMO'
        ],
        targetFields: [
            '(Unmapped)', 'date', 'customerId', 'amount', 'type', 
            'counterpartyName', 'counterpartyBank', 'counterpartyCountry', 'memo'
        ],
        currentMap: {
            'TRN_DATE': 'date',
            'CUST_ID': 'customerId',
            'TRN_AMT_LCY': 'amount',
            'TRN_TYPE_CD': 'type',
            'BEN_NAME': 'counterpartyName',
            'BEN_BANK': 'counterpartyBank',
            'BENEFICIARY_COUNTRY': 'counterpartyCountry',
            'MEMO': 'memo',
        }
    }
];
/* const initialRiskModels = [
    {
        id: 'customer_risk_model',
        factors: {
            occupations: {
                'Used Car Dealer': 40,
                'International Trade': 35,
                'Software Engineer': 5,
                'Retail': 15,
            },
            nationalities: {
                'USA': 5,
                'CAN': 5,
                'SYR': 80, // High-risk country
            }
        }
    }
]; */
export const initialRiskModels = [
    { 
      id: 'customer_risk_model', 
      factors: {
        occupations: {
            'Real Estate Developer': 40, 'International Trade': 35, 'Oil & Gas Executive': 30,
            'Precious Metals Dealer': 60, 'Private Equity Firm': 55, 'Government Official': 70,
            'Logistics Company': 25, 'Marketing Director': 10, 'Luxury Goods Dealer': 35,
            'Construction Contractor': 30, 'Restaurant Owner': 15, 'IT Services Company': 15,
            'Art Dealer': 40, 'Manufacturing Manager': 20, 'Travel Agency': 20,
            'IT Consultant': 5, 'Teacher': 5, 'Doctor': 5, 'Accountant': 10,
            'Retail': 10, 'University Professor': 5, 'Graphic Designer': 5,
            'Chef': 10, 'Pharmacist': 5, 'Pilot': 10, 'Hotel Manager': 10, 'Engineer': 5, 'Architect': 10
        },
        nationalities: { 'SAU': 5, 'ARE': 5, 'GBR': 5, 'RUS': 20, 'CHN': 15, 'IND': 5, 'EGY': 10, 'AUS': 5, 'FRA': 5, 'JOR': 10, 'SYR': 80, 'IRN': 90, 'YEM': 75, 'AFG': 70, 'PRK': 95 }
      }
    }
];
const initialMlModels = [
    { id: 'txn-risk-model', name: 'Transaction Anomaly Detector', version: '1.0', baseScoreAddition: 5.0 }
];
/* const initialWatchlists = [
    {
        id: 'un-sanctions',
        name: 'UN Sanctions List',
        type: 'Entity',
        entries: [
            { name: 'Global Trading Co', nationality: 'SYR', dob: null },
            { name: 'John Smith', nationality: 'AFG', dob: '1975-01-10' },
            { name: 'Anna Petrova', nationality: 'RUS', dob: '1982-03-15' },
        ]
    },
    {
        id: 'suspicious-keywords',
        name: 'Suspicious Transaction Keywords',
        type: 'Keyword',
        entries: [ 'ammo', 'bearer bonds', 'structuring', 'offshore shell' ]
    }
]; */
const initializeDatabase = async () => {
  const populateStore = async (storeName, initialData) => {
    const count = (await dbService.getAll(storeName)).length;
    if (count === 0) {
      console.log(`Populating '${storeName}' with initial data...`);
      for (const item of initialData) {
        await dbService.add(storeName, item);
      }
    }
  };

  await populateStore('users', initialUsers);
  await populateStore('cases', initialCases);
  await populateStore('rules', initialRules);
  await populateStore('customers', initialCustomers);
  await populateStore('permissions', initialPermissions); // Add permissions to DB
  await populateStore('ml_models', initialMlModels); // New line
  await populateStore('mappings', initialMappings); // New line
  await populateStore('risk_models', initialRiskModels); // New line
  await populateStore('watchlists', initialWatchlists); // New line

  const mastersCount = (await dbService.getAll('masters')).length;
  if (mastersCount === 0) {
    await dbService.add('masters', { id: 'highRiskCountries', data: ['North Korea', 'Iran', 'Syria'] });
    await dbService.add('masters', { id: 'transactionTypes', data: ['Cash Deposit', 'Wire Transfer', 'ACH Payment'] });
  }
};

function App() {
  useEffect(() => {
    initializeDatabase();
  }, []);

  return (
    <UIProvider>
      <AuthProvider>
        <ToastContainer position="bottom-right" autoClose={4000} hideProgressBar={false} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/*" element={
              <PrivateRoute>
                <MainLayout>
                  <Routes>
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="alert-queue" element={<AlertQueue />} />
                    {/* <Route path="transaction-generator" element={<TransactionGenerator />} /> */}
                    <Route path="transaction-analysis" element={<TransactionAnalysis />} />
                    <Route path="case-management" element={<CaseList />} />
                    <Route path="case-management/:caseId" element={<CaseDetail />} />
                    <Route path="kyc-profile/:customerId" element={<KYCProfile />} />
                    <Route path="risk-management" element={<RiskManagementStudio />} />
                    <Route path="user-administration" element={<UserList />} />
                    <Route path="reporting" element={<ReportDashboard />} />
                    <Route path="rule-engine" element={<RuleList />} />
                    <Route path="screening-studio" element={<ScreeningStudio />} /> {/* Updated Route */}
                    <Route path="data-mapping" element={<DataMapping />} />
                    <Route path="masters" element={<Masters />} />
                    <Route path="link-analysis" element={<LinkAnalysis />} />
                    <Route path="link-analysis/:customerId" element={<LinkAnalysis />} />
                    <Route path="integrations" element={<Integrations />} />
                    <Route path="system-simulator" element={<SystemSimulator />} /> {/* New Unified Route */}
                    {/* <Route path="demo-data" element={<DemoDataGenerator />} /> */}
                    <Route path="audit-log" element={<AuditLog />} />
                    <Route path="permission-management" element={<PermissionManagement />} />
                    <Route path="automation-studio" element={<AutomationStudio />} />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="*" element={<Navigate to="/dashboard" />} />
                  </Routes>
                </MainLayout>
              </PrivateRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </UIProvider>
  );
}

export default App;